import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import PropertyCard from "@/components/PropertyCard";
import { CITIES, PROPERTY_TYPES } from "@/lib/format";

const SALE_BUDGETS = [
  { label: "حتى مليون", min: 0, max: 1000000 },
  { label: "1 – 3 ملايين", min: 1000000, max: 3000000 },
  { label: "3 – 5 ملايين", min: 3000000, max: 5000000 },
  { label: "أكثر من 5 ملايين", min: 5000000, max: "" },
];
const RENT_BUDGETS = [
  { label: "حتى 60 ألف", min: 0, max: 60000 },
  { label: "60 – 120 ألف", min: 60000, max: 120000 },
  { label: "120 – 200 ألف", min: 120000, max: 200000 },
  { label: "أكثر من 200 ألف", min: 200000, max: "" },
];
const SORTS = [
  { value: "newest", label: "الأحدث" },
  { value: "price_asc", label: "السعر: من الأقل" },
  { value: "price_desc", label: "السعر: من الأعلى" },
  { value: "area_desc", label: "المساحة: الأكبر" },
];

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const purpose = searchParams.get("purpose") || "";
  const budgets = purpose === "rent" ? RENT_BUDGETS : SALE_BUDGETS;
  const budgetValue = useMemo(() => {
    const min = searchParams.get("min_price");
    const max = searchParams.get("max_price");
    if (min === null && max === null) return "";
    const hit = budgets.find((b) => String(b.min) === (min ?? "") && String(b.max) === (max ?? ""));
    return hit ? `${hit.min}-${hit.max}` : "";
  }, [searchParams, budgets]);

  useEffect(() => {
    setLoading(true);
    setError("");
    const params = Object.fromEntries(searchParams.entries());
    api
      .get("/properties", { params })
      .then((r) => setData(r.data))
      .catch(() => setError("تعذر تحميل العقارات، حاول مرة أخرى"))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const set = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "purpose") {
      next.delete("min_price");
      next.delete("max_price");
    }
    setSearchParams(next);
  };

  const setBudget = (v) => {
    const next = new URLSearchParams(searchParams);
    next.delete("min_price");
    next.delete("max_price");
    if (v) {
      const [min, max] = v.split("-");
      if (min) next.set("min_price", min);
      if (max) next.set("max_price", max);
    }
    setSearchParams(next);
  };

  const get = (k) => searchParams.get(k) || "";

  return (
    <>
      <Seo title="العقارات" description="تصفح عقارات افتراضية للبيع والإيجار في جدة والرياض والدمام مع فلاتر بحث متقدمة — نموذج تجريبي." />
      <section className="bg-forest py-16 md:py-24">
        <div className="container-lux space-y-4">
          <p className="text-gold text-sm font-heading font-semibold tracking-widest">قائمة العقارات</p>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-ivory tracking-tight">ابحث عن عقارك المثالي</h1>
          <p className="text-ivory/70 max-w-xl">جميع العقارات المعروضة افتراضية لأغراض العرض التجريبي.</p>
        </div>
      </section>

      <section className="container-lux py-10 md:py-14" data-testid="listings-section">
        <div className="glass rounded-2xl p-4 md:p-6 mb-10 space-y-4" data-testid="filters-bar">
          <div className="flex items-center gap-2 text-forest font-heading font-bold text-sm">
            <SlidersHorizontal className="h-4 w-4 text-gold-dark" aria-hidden="true" />
            تصفية النتائج
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <select className="input-lux" value={get("city")} onChange={(e) => set("city", e.target.value)} aria-label="المدينة" data-testid="filter-city">
              <option value="">كل المدن</option>
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="input-lux" value={purpose} onChange={(e) => set("purpose", e.target.value)} aria-label="الغرض" data-testid="filter-purpose">
              <option value="">بيع وإيجار</option>
              <option value="sale">للبيع</option>
              <option value="rent">للإيجار</option>
            </select>
            <select className="input-lux" value={get("ptype")} onChange={(e) => set("ptype", e.target.value)} aria-label="نوع العقار" data-testid="filter-type">
              <option value="">كل الأنواع</option>
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="input-lux" value={budgetValue} onChange={(e) => setBudget(e.target.value)} aria-label="الميزانية" data-testid="filter-budget">
              <option value="">أي ميزانية</option>
              {budgets.map((b) => <option key={b.label} value={`${b.min}-${b.max}`}>{b.label}</option>)}
            </select>
            <select className="input-lux" value={get("beds")} onChange={(e) => set("beds", e.target.value)} aria-label="غرف النوم" data-testid="filter-beds">
              <option value="">أي عدد غرف</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+ غرف</option>)}
            </select>
            <select className="input-lux" value={get("sort") || "newest"} onChange={(e) => set("sort", e.target.value === "newest" ? "" : e.target.value)} aria-label="الترتيب" data-testid="filter-sort">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-destructive/10 text-destructive px-5 py-4 text-sm mb-8" data-testid="listings-error">{error}</div>
        )}

        {!loading && !error && (
          <p className="text-sm text-muted-foreground mb-6" data-testid="listings-count">
            {data.count > 0 ? `${data.count} عقار مطابق` : ""}
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => <div key={n} className="h-80 animate-pulse rounded-2xl bg-forest/5" />)}
          </div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24 text-center" data-testid="listings-empty">
            <SearchX className="h-14 w-14 text-forest/30" aria-hidden="true" />
            <h2 className="font-heading font-bold text-xl text-forest">لا توجد نتائج مطابقة</h2>
            <p className="text-sm text-muted-foreground max-w-sm">جرّب توسيع نطاق البحث أو إزالة بعض عوامل التصفية.</p>
            <button className="btn-outline-forest text-sm" onClick={() => setSearchParams({})} data-testid="filters-reset-button">
              إعادة تعيين البحث
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {data.items.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>
    </>
  );
}
